import React, { useState } from 'react';
import { Badge, Row, Col, Alert } from 'react-bootstrap';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
    FaEdit,
    FaSave,
    FaTimes,
    FaUser,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBriefcase,
    FaFileAlt,
    FaImage,
    FaCalculator,
    FaBan,
    FaExclamationTriangle,
    FaCheckCircle,
    FaTimesCircle,
    FaCloudUploadAlt,
    FaSpinner,
    FaClock,
    FaExpand
} from 'react-icons/fa';
import useClientEOIDetail from './hooks/useClientEOIDetail';

const ClientEOIDetailModal = ({ eoi, show, onClose, onUpdate }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const {
        isEditing,
        setIsEditing,
        isLoading,
        formData,
        setFormData,
        calculatedAmount,
        setCalculatedAmount,
        uploadedFiles,
        setUploadedFiles,
        uploadErrors,
        setUploadErrors,
        isDragging,
        currentUser,
        getStatusBadge,
        formatDateForDisplay,
        formatDateForInput,
        formatCurrency,
        getBasePrice,
        getMediaUrl,
        handleInputChange,
        handleContractDurationBlur,
        handleFileUpload,
        removeFile,
        handleSave,
        handleCancelRequest,
        Dropzone
    } = useClientEOIDetail({ eoi, show, onClose, onUpdate });

    if (!eoi || !show) return null;

    const professional = eoi.professional;
    const user = professional?.user;

    // Fonction pour annuler l'édition
    const handleCancelEdit = () => {
        setIsEditing(false);
        setUploadedFiles([]);
        setUploadErrors([]);
        const originalServiceFee = eoi.serviceFee || 0;
        const originalContractDuration = eoi.contractDuration || 1;
        setFormData({
            desiredStartDate: formatDateForInput(eoi.desiredStartDate || eoi.professional?.desiredStartDate),
            contractDuration: originalContractDuration,
            serviceLocation: eoi.serviceLocation || '',
            comments: eoi.comments || '',
            serviceFee: originalServiceFee
        });
        setCalculatedAmount(originalServiceFee * originalContractDuration);
    };

    // ✅ Fonction pour ouvrir Lightbox
    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    // ✅ Récupérer toutes les images pour Lightbox
    const getLightboxImages = () => {
        if (!eoi.mediaSubscriptions) return [];
        return eoi.mediaSubscriptions.map(media => ({
            src: getMediaUrl(media),
            alt: media.name || 'Image'
        }));
    };

    // ✅ Gestion du handleSave avec fermeture auto
    const handleSaveAndClose = async () => {
        await handleSave();
        // La modal se ferme automatiquement après le succès
        // (la fermeture est gérée dans handleSave via onClose)
    };

    return (
        <>
            <div
                className="eoi-detail-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="eoi-detail-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaFileAlt className="me-2 text-primary" />
                                    Détails de votre demande
                                </h4>
                                <div className="mt-2">
                                    {getStatusBadge(eoi.clientEOIStatus)}
                                </div>
                            </div>
                            <button className="btn-close" onClick={onClose} disabled={isLoading}></button>
                        </div>
                    </div>

                    {/* Contenu */}
                    <div className="p-4">
                        {/* Informations du professionnel */}
                        {!isEditing && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-primary">
                                    <FaUser className="me-2" />
                                    Informations du professionnel
                                </h5>
                                <Row className="g-3">
                                    <Col md={3} className="text-center">
                                        {user?.profilePictureUrl ? (
                                            <img
                                                src={user.profilePictureUrl}
                                                alt="Profile"
                                                className="rounded-circle mb-2"
                                                style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                    border: '3px solid #28a745'
                                                }}
                                            />
                                        ) : (
                                            <div className="bg-white rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2"
                                                 style={{ width: '100px', height: '100px', border: '3px solid #28a745' }}>
                                                <FaUser size={40} className="text-muted" />
                                            </div>
                                        )}
                                        <h6 className="text-success fw-bold mt-2">{user?.username}</h6>
                                        <Badge bg="success" className="rounded-pill">
                                            {professional?.selectedService}
                                        </Badge>
                                    </Col>
                                    <Col md={9}>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <div className="display-field">
                                                    <div className="field-label">
                                                        <FaUser className="me-1" /> Nom complet
                                                    </div>
                                                    <div className="field-value bg-white rounded p-2">
                                                        {user?.firstName} {user?.lastName}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="display-field">
                                                    <div className="field-label">
                                                        <FaMapMarkerAlt className="me-1" /> Localisation
                                                    </div>
                                                    <div className="field-value bg-white rounded p-2">
                                                        {user?.neighborhood && `${user.neighborhood}, `}
                                                        {user?.city}, {user?.country}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="display-field">
                                                    <div className="field-label">
                                                        <FaClock className="me-1" /> Expérience
                                                    </div>
                                                    <div className="field-value bg-white rounded p-2">
                                                        {professional?.yearsOfExperience || 0} ans
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* Détails du service */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaBriefcase className="me-2" />
                                Détails du service demandé
                            </h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="display-field">
                                        <div className="field-label">Service principal</div>
                                        <div className="field-value bg-white rounded p-2">
                                            <strong>{eoi.serviceName || professional?.selectedService}</strong>
                                        </div>
                                    </div>
                                    <div className="display-field mt-3">
                                        <div className="field-label">Sous-service</div>
                                        <div className="field-value bg-white rounded p-2">
                                            {eoi.subServiceName || 'Non spécifié'}
                                        </div>
                                    </div>
                                    <div className="display-field mt-3">
                                        <div className="field-label">
                                            <FaMapMarkerAlt className="me-1 text-danger" /> Localisation du service *
                                        </div>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={formData.serviceLocation}
                                                onChange={(e) => handleInputChange('serviceLocation', e.target.value)}
                                                placeholder="Adresse du service"
                                                className="form-control"
                                                disabled={isLoading}
                                            />
                                        ) : (
                                            <div className="field-value bg-white rounded p-2">
                                                {formData.serviceLocation || eoi.serviceLocation || `${user?.city}, ${user?.country}`}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="display-field">
                                        <div className="field-label">
                                            <FaCalendarAlt className="me-1 text-primary" /> Date de début souhaitée *
                                        </div>
                                        {isEditing ? (
                                            <input
                                                type="date"
                                                value={formData.desiredStartDate}
                                                onChange={(e) => handleInputChange('desiredStartDate', e.target.value)}
                                                min={new Date().toISOString().split('T')[0]}
                                                className="form-control"
                                                disabled={isLoading}
                                            />
                                        ) : (
                                            <div className="field-value bg-white rounded p-2">
                                                {formatDateForDisplay(eoi.desiredStartDate || professional?.desiredStartDate)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="display-field mt-3">
                                        <div className="field-label">Durée du contrat *</div>
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={formData.contractDuration === '' ? '' : formData.contractDuration}
                                                onChange={(e) => handleInputChange('contractDuration', e.target.value)}
                                                onBlur={handleContractDurationBlur}
                                                placeholder="Durée en mois"
                                                className="form-control"
                                                disabled={isLoading}
                                            />
                                        ) : (
                                            <div className="field-value bg-white rounded p-2">
                                                {formData.contractDuration || eoi.contractDuration || professional?.desiredContractDuration} mois
                                            </div>
                                        )}
                                    </div>
                                    <div className="display-field mt-3">
                                        <div className="field-label">
                                            <FaCalculator className="me-1 text-success" /> Tarif du service
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            <span className="text-success fw-bold">
                                                {formatCurrency(isEditing ? formData.serviceFee : (eoi.serviceFee || 0))}
                                            </span>
                                        </div>
                                        {isEditing && (
                                            <small className="text-muted">Prix calculé automatiquement en fonction de la durée</small>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {isEditing && calculatedAmount > 0 && (
                                <Alert variant="success" className="mt-3 rounded-3">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <div>
                                            <FaCalculator className="me-2" />
                                            <strong>Détail du calcul:</strong>
                                        </div>
                                        <div>
                                            <span className="h5 text-success fw-bold mb-0">
                                                {formatCurrency(calculatedAmount)}
                                            </span>
                                        </div>
                                    </div>
                                    {getBasePrice() > 0 && (
                                        <small className="text-muted d-block mt-2">
                                            {formatCurrency(getBasePrice())} (prix de base) × {formData.contractDuration} mois = {formatCurrency(calculatedAmount)}
                                        </small>
                                    )}
                                </Alert>
                            )}
                        </div>

                        {/* Galerie des médias existants avec Lightbox */}
                        {!isEditing && eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0 && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-warning">
                                    <FaImage className="me-2" />
                                    Galerie des médias
                                </h5>
                                <Row className="g-3">
                                    {eoi.mediaSubscriptions.map((media, index) => (
                                        <Col md={4} key={media.id || index}>
                                            <div
                                                className="rounded-3 overflow-hidden shadow-sm position-relative"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openLightbox(index)}
                                            >
                                                <img
                                                    src={getMediaUrl(media)}
                                                    alt={media.name}
                                                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                                    className="img-fluid"
                                                    onError={(e) => {
                                                        e.target.src = "/assets/images/default-service.jpg";
                                                    }}
                                                />
                                                <div
                                                    className="position-absolute top-0 end-0 m-2 bg-dark bg-opacity-50 rounded-circle p-1"
                                                    style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <FaExpand size={14} color="white" />
                                                </div>
                                                <div className="bg-white p-2 text-center">
                                                    <small className="text-muted">{media.name}</small>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* Section Dropzone pour les nouveaux fichiers */}
                        {isEditing && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-warning">
                                    <FaImage className="me-2" />
                                    Ajouter des médias
                                </h5>
                                <p className="text-muted small mb-3">
                                    Optionnel - images uniquement, max 3 images, 10MB par image
                                </p>

                                {eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">
                                            Images actuelles (conservées si aucune nouvelle image n'est ajoutée) :
                                        </small>
                                        <Row className="g-2">
                                            {eoi.mediaSubscriptions.map((media, index) => (
                                                <Col xs={4} key={media.id || index}>
                                                    <img
                                                        src={getMediaUrl(media)}
                                                        alt={media.name}
                                                        className="img-thumbnail rounded-3"
                                                        style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.src = "/assets/images/default-service.jpg";
                                                        }}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {uploadedFiles.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">Nouvelles images à uploader :</small>
                                        <Row className="g-2">
                                            {uploadedFiles.map((file, index) => (
                                                <Col xs={4} key={index}>
                                                    <div className="position-relative">
                                                        <img
                                                            src={URL.createObjectURL(file)}
                                                            alt={`Upload ${index + 1}`}
                                                            className="img-thumbnail rounded-3"
                                                            style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                                                        />
                                                        <button
                                                            className="btn btn-danger btn-sm position-absolute top-0 end-0 rounded-circle"
                                                            style={{ transform: 'translate(30%, -30%)', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                            onClick={() => removeFile(index)}
                                                            disabled={isLoading}
                                                        >
                                                            <FaTimes size={12} />
                                                        </button>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                        <small className="text-muted d-block mt-2">
                                            {uploadedFiles.length}/3 images sélectionnées
                                        </small>
                                    </div>
                                )}

                                {uploadedFiles.length < 3 && (
                                    <Dropzone>
                                        <FaCloudUploadAlt
                                            size={48}
                                            color={isDragging ? '#28a745' : '#6c757d'}
                                            style={{ marginBottom: '1rem', transition: 'color 0.3s ease' }}
                                        />
                                        <p className="mb-0 fw-medium" style={{ color: isDragging ? '#28a745' : '#495057' }}>
                                            {isDragging ? 'Déposez vos images ici' : 'Glissez-déposez vos images ici'}
                                        </p>
                                        <p className="text-muted small mb-0">
                                            ou cliquez pour parcourir (images uniquement)
                                        </p>
                                        <div className="mt-2">
                                            <Badge bg="light" text="dark" className="me-1">JPG</Badge>
                                            <Badge bg="light" text="dark" className="me-1">PNG</Badge>
                                            <Badge bg="light" text="dark" className="me-1">GIF</Badge>
                                            <Badge bg="light" text="dark">WEBP</Badge>
                                        </div>
                                        {uploadErrors.length > 0 && (
                                            <div className="mt-2 text-danger small">
                                                {uploadErrors.map((err, i) => (
                                                    <div key={i}>• {err}</div>
                                                ))}
                                            </div>
                                        )}
                                    </Dropzone>
                                )}
                            </div>
                        )}

                        {/* Commentaires */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">
                                <FaEdit className="me-2" />
                                Votre message
                            </h5>
                            <div className="display-field">
                                <div className="field-label">Commentaires *</div>
                                {isEditing ? (
                                    <textarea
                                        rows={4}
                                        value={formData.comments}
                                        onChange={(e) => handleInputChange('comments', e.target.value)}
                                        placeholder="Ajoutez vos commentaires ou questions pour ce professionnel..."
                                        className="form-control"
                                        disabled={isLoading}
                                    />
                                ) : (
                                    <div className="field-value bg-white rounded p-3">
                                        {formData.comments || eoi.comments}
                                    </div>
                                )}
                                <small className="text-muted">Ce message sera visible par le professionnel.</small>
                            </div>
                        </div>

                        {/* Motif du rejet ou de l'annulation */}
                        {(eoi.clientEOIStatus === 'REJECTED' || eoi.clientEOIStatus === 'CANCELLED') && eoi.rejectionReason && (
                            <div className={`rounded-3 p-3 mb-4 ${eoi.clientEOIStatus === 'CANCELLED' ? 'bg-secondary bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                                <h5 className={`fw-bold mb-2 ${eoi.clientEOIStatus === 'CANCELLED' ? 'text-secondary' : 'text-danger'}`}>
                                    <FaExclamationTriangle className="me-2" />
                                    {eoi.clientEOIStatus === 'CANCELLED' ? "Motif de l'annulation" : "Motif du rejet"}
                                </h5>
                                <div className="bg-white rounded p-3 border">
                                    {eoi.rejectionReason}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer avec actions */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px', marginTop: 'auto'
                    }}>
                        {!isEditing ? (
                            <div className="justify-content-between align-items-center w-100">
                                {eoi.clientEOIStatus === 'PENDING' && (
                                    <div className="d-flex gap-3">
                                        <button
                                            className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            <FaEdit /> Modifier la demande
                                        </button>
                                        <button
                                            className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2"
                                            onClick={handleCancelRequest}
                                            disabled={isLoading}
                                        >
                                            <FaBan /> Annuler la demande
                                        </button>
                                    </div>
                                )}

                                {eoi.clientEOIStatus === 'APPROVED' && (
                                    <Alert variant="success" className="mb-0 w-100 rounded-pill text-center">
                                        <FaCheckCircle className="me-2" />
                                        Cette demande a été approuvée et ne peut plus être modifiée.
                                    </Alert>
                                )}

                                {eoi.clientEOIStatus === 'REJECTED' && (
                                    <Alert variant="danger" className="mb-0 w-100 rounded-pill text-center">
                                        <FaTimesCircle className="me-2" />
                                        Cette demande a été rejetée et ne peut plus être modifiée.
                                    </Alert>
                                )}

                                {eoi.clientEOIStatus === 'CANCELLED' && (
                                    <Alert variant="secondary" className="mb-0 w-100 rounded-pill text-center">
                                        <FaBan className="me-2" />
                                        Cette demande a été annulée et ne peut plus être modifiée.
                                    </Alert>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex justify-content-end gap-3 w-100">
                                <button
                                    className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                    onClick={handleCancelEdit}
                                    disabled={isLoading}
                                >
                                    <FaTimes /> Annuler
                                </button>
                                <button
                                    className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                    onClick={handleSaveAndClose}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <FaSpinner className="fa-spin me-2" />
                                            Enregistrement...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave /> Enregistrer
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ✅ Lightbox pour afficher les images en grand format */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={getLightboxImages()}
                index={lightboxIndex}
                styles={{
                    container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
                    button: { color: 'white' }
                }}
            />

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }

                .eoi-detail-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }
            `}</style>
        </>
    );
};

export default ClientEOIDetailModal;